import {
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Toolbar,
  InputAdornment,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TableSortLabel,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { useDeleteContact } from '@/api/endpoints/contact/useDeleteContact';
import {
  useGetAllContacts,
  Contact,
  ContactDetail,
} from '@/api/endpoints/contact/useGetAllContacts';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { DotsVerticalIcon } from '@/assets/icons/DotsVerticalIcon';
import { EyeIcon } from '@/assets/icons/EyeIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import { ConfirmActionModal } from '@/components/confirm/ConfirmActionModal';

interface ContactMenuState {
  anchorEl: HTMLElement | null;
  contactId: number | null;
  contactName: string;
}

type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'name' || orderBy === 'type') {
    const aValue = String(a[orderBy]).toLowerCase();
    const bValue = String(b[orderBy]).toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<T>(order: Order, orderBy: keyof T) {
  return order === 'desc'
    ? (a: T, b: T) => descendingComparator(a, b, orderBy)
    : (a: T, b: T) => -descendingComparator(a, b, orderBy);
}

interface ContactToDelete {
  id: number;
  name: string;
}

const ContactListPage: React.FC = () => {
  const { selectedPropertyLocation } = usePropertyManager();
  const history = useHistory();
  const theme = useTheme();
  const [menuState, setMenuState] = useState<ContactMenuState>({
    anchorEl: null,
    contactId: null,
    contactName: '',
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<ContactToDelete | null>(null);

  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Contact>('name');
  const getAllContactsQuery = useGetAllContacts();

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const filteredData = useMemo(() => {
    let filtered = getAllContactsQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter((contact: Contact) =>
        contact.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllContactsQuery.data, filterName, order, orderBy]);

  const deleteContactMutation = useDeleteContact();

  const handleSort = (property: keyof Contact) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleAddContact = () => {
    // Navigate to create contact page
    history.push(PMPRoutes.contactCreate);
  };

  const handleContactClick = (contactId: number) => {
    history.push(PMPRoutes.contactView.replace(':id', contactId.toString()));
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    contactId: number,
    contactName: string
  ) => {
    event.stopPropagation(); // Prevent row click
    setMenuState({
      anchorEl: event.currentTarget,
      contactId,
      contactName,
    });
  };

  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      contactId: null,
      contactName: '',
    });
  };

  const handleViewContact = () => {
    if (menuState.contactId) {
      history.push(PMPRoutes.contactView.replace(':id', menuState.contactId.toString()));
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (menuState.contactId) {
      setContactToDelete({
        id: menuState.contactId,
        name: menuState.contactName,
      });
      setDeleteConfirmOpen(true);
      handleMenuClose();
    }
  };

  const handleDeleteContact = () => {
    if (contactToDelete) {
      deleteContactMutation.mutate({ contactId: contactToDelete.id });
    }
    setDeleteConfirmOpen(false);
    setContactToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setContactToDelete(null);
  };

  const getAvatarProps = (name: string) => {
    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    // Generate a consistent color based on the name
    const colors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'] as const;
    const colorIndex = name.length % colors.length;

    return {
      children: initials,
      sx: {
        bgcolor: `${colors[colorIndex]}.main`,
        color: 'white',
        width: 32,
        height: 32,
        fontSize: '0.875rem',
      },
    };
  };

  const getContactEmail = (details: ContactDetail[]) => {
    const emailDetail = details.find(detail => detail.type === 'email');
    return emailDetail?.value || '';
  };

  const getContactPhone = (details: ContactDetail[]) => {
    const phoneDetail = details.find(detail => detail.type === 'phone');
    return phoneDetail?.value || '';
  };

  const getJobTitleAndCompany = (contact: Contact) => {
    // Extract job title and company from details or role
    const jobDetail = contact.details.find(
      (detail: ContactDetail) =>
        detail.type === 'other' &&
        (detail.label?.toLowerCase().includes('job') ||
          detail.label?.toLowerCase().includes('title') ||
          detail.label?.toLowerCase().includes('company'))
    );

    if (jobDetail) {
      return jobDetail.value;
    }

    // Fallback to role if no specific job detail
    return contact.role !== 'OTHER' ? contact.role.replace(/_/g, ' ') : '';
  };

  if (!selectedPropertyLocation) {
    return (
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
        <Typography variant="body2" color="text.secondary">
          Please select a property location to view contacts.
        </Typography>
      </Stack>
    );
  }

  const contactsCount = filteredData.length;

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
      <DashboardHeader title={`Contacts (${contactsCount})`}>
        <Button
          color="primary"
          variant="contained"
          size="small"
          startIcon={<PlusIcon />}
          onClick={handleAddContact}
        >
          Add Contact
        </Button>
      </DashboardHeader>

      <Box sx={{ flex: 1 }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search contacts..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        {getAllContactsQuery.isLoading && (
          <Typography variant="body2" color="text.secondary">
            Loading contacts...
          </Typography>
        )}

        {getAllContactsQuery.isError && (
          <Typography variant="body2" color="error">
            Error loading contacts: {getAllContactsQuery.error?.message}
          </Typography>
        )}

        {getAllContactsQuery.data && (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: '0px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.secondary[200] }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      textTransform: 'none',
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      textTransform: 'none',
                    }}
                  >
                    Phone number
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      textTransform: 'none',
                    }}
                  >
                    Job title & company
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'role'}
                      direction={orderBy === 'role' ? order : 'asc'}
                      onClick={() => handleSort('role')}
                    >
                      Role
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {filterName
                          ? 'No contacts found matching your search.'
                          : 'No contacts found. Click "Add Contact" to create your first contact.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(contact => (
                      <TableRow
                        key={contact.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: `${theme.palette.secondary[200]} !important`,
                            '& .MuiTableCell-root': {
                              color: 'white !important',
                            },
                          },
                          '&:not(:last-child)': {
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          },
                        }}
                        onClick={() => handleContactClick(contact.id)}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar {...getAvatarProps(contact.name)} />
                            <Typography
                              variant="body3"
                              color="text.primary"
                              sx={{
                                '&:hover': {
                                  color: theme.palette.primary.main,
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {contact.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {getContactEmail(contact.details)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {getContactPhone(contact.details)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body3" color="text.primary">
                            {getJobTitleAndCompany(contact)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Chip
                              label={contact.role.replace(/_/g, ' ')}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem', height: 24 }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={e => handleMenuOpen(e, contact.id, contact.name)}
                          >
                            <DotsVerticalIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={menuState.anchorEl}
        open={Boolean(menuState.anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleViewContact}>
          <ListItemIcon>
            <EyeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteConfirm} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Trash01Icon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && contactToDelete && (
        <ConfirmActionModal
          title="Delete Contact"
          onConfirm={handleDeleteContact}
          onReject={handleDeleteCancel}
        >
          <Typography>
            Are you sure you want to delete "{contactToDelete.name}"? This action cannot be undone.
          </Typography>
        </ConfirmActionModal>
      )}
    </Stack>
  );
};

export default ContactListPage;
